import { ApiError } from '../api/client'
import { t, type Key } from './dict'

/**
 * A failure, in the trader's language, with the server's own words kept underneath.
 *
 * The API answers with RFC 7807 problem documents whose `detail` is English prose composed in
 * C#. Translating that would mean an `Accept-Language` the daemon cannot honour for queued work
 * and a second place each message is written — the same argument as everywhere else in
 * `docs/12-plugin-i18n.md`. What the client *can* do is recognise the shape: a 401 is a session
 * that ended, a 503 is the guardian declining to decide, and neither needs the server's sentence
 * to be understood.
 *
 * So the headline is local and the server's `detail` follows as fine print. It is the line the
 * trader quotes to the operator, so it stays verbatim rather than being dropped — an error that
 * reads well and cannot be reported is worse than one that does not.
 */
export interface Problem {
  /** Translated. What went wrong, in one sentence. */
  readonly message: string
  /** The server's own English, or null when there was no response to read one from. */
  readonly detail: string | null
}

export function problem(message: string, detail: string | null = null): Problem {
  return { message, detail }
}

/**
 * The status is the whole classification, except for 404 — whose meaning depends on what was
 * asked for. That one call sites name with `notFound`.
 */
export function describeError(error: unknown, notFound: Key = 'error.notFound'): Problem {
  // Not an ApiError at all: fetch rejected, so there is no response and no status. This is the
  // ordinary case of the box being unreachable, and it must not read as a bug in the plugin.
  if (!(error instanceof ApiError)) {
    return problem(t('error.offline'), error instanceof Error ? error.message : null)
  }

  const detail = error.detail || error.title || null

  switch (error.status) {
    case 401:
      return problem(t('error.signedOut'), detail)
    case 403:
      return problem(t('error.forbidden'), detail)
    case 404:
      return problem(t(notFound), detail)
    case 429:
      return problem(t('error.tooMany'), detail)
    case 503:
      return problem(t('error.unavailable'), detail)
    default:
      return problem(
        t(error.status >= 500 ? 'error.server' : 'error.unexpected', { status: error.status }),
        detail,
      )
  }
}
